import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { activateInvitedUser, completeInvitedUserSetup } from "@/integrations/backend/auth";
import frequentHealthIcon from "@/assets/frequent-health-icon.jpeg";

export default function InvitedUserSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "form" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [code, setCode] = useState<string>("");
  
  // Form state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const codeParam = searchParams.get("code");
    
    if (!codeParam) {
      setStatus("error");
      setErrorMessage("Invalid activation link. No code provided.");
      return;
    }

    setCode(codeParam);

    const activate = async () => {
      try {
        await activateInvitedUser(codeParam);
        setStatus("form");
        toast.success("Account activated! Please complete your setup.");
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.message || "Failed to activate account. Please contact support.");
        toast.error(error.message || "Activation failed");
      }
    };

    activate();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword || !firstName || !lastName) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setIsSubmitting(true);

    try {
      await completeInvitedUserSetup(code, password, firstName, lastName);
      setStatus("success");
      toast.success("Setup completed successfully!");
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to complete setup. Please try again.");
      toast.error(error.message || "Setup failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MarketingHeader />
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4 pt-24">
        <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img
                src={frequentHealthIcon}
                alt="Frequent Health logo"
                className="h-12 w-auto"
              />
            </div>
            <CardTitle className="text-2xl text-white">
              {status === "loading" && "Loading user account..."}
              {status === "form" && "Complete Your Account Setup"}
              {status === "success" && "Setup Complete!"}
              {status === "error" && "Activation Failed"}
            </CardTitle>
            <p className="text-gray-light">
              {status === "loading" && "Please wait while we set up your account..."}
              {status === "form" && "Set your password and enter your name to get started"}
              {status === "success" && "Your account is ready! Redirecting to login..."}
              {status === "error" && errorMessage}
            </p>
          </CardHeader>
          <CardContent>
            {status === "loading" && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            )}
            
            {status === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="first-name" className="text-gray-light">First Name</Label>
                  <Input
                    id="first-name"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last-name" className="text-gray-light">Last Name</Label>
                  <Input
                    id="last-name"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-light">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    minLength={8}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-gray-light">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    minLength={8}
                    className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all"
                >
                  {isSubmitting ? "Completing Setup..." : "Complete Setup"}
                </Button>
              </form>
            )}

            {status === "success" && (
              <div className="text-center py-4">
                <div className="text-4xl mb-4">✓</div>
                <p className="text-gray-light">You will be redirected to the login page shortly.</p>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-4">
                <div className="text-4xl mb-4 text-red-500">✗</div>
                <p className="text-gray-light mb-4">{errorMessage}</p>
                <button
                  onClick={() => navigate("/login")}
                  className="text-primary hover:text-lime-hover font-medium"
                >
                  Go to Login
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <MarketingFooter />
    </>
  );
}
